import { Box, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { View } from '../../types'

import { RouteManager } from '@workwave-tidal/core/icons/logos'
import type { LoginFormApi, LoginFormState } from '@workwave-tidal/core/login-ui'
import { ForgotPasswordForm, LoginForm } from '@workwave-tidal/core/login-ui'
import { LoginCallToAction } from '@workwave-tidal/core/login-ui/LoginCallToAction'
import { LoginFormContainer } from '@workwave-tidal/core/login-ui/LoginFormContainer'

import { useQueryParameters } from '@/hooks'

import { useLoginActions } from '../hooks/useLoginActions'
import { useLoginInitialValues } from '../hooks/useLoginInitialValues'
import { useLoginThrottle } from '../hooks/useLoginThrottle'
import { useTexts } from '../hooks/useTexts'
import { ThrottleAlerts } from './ThrottleAlerts'

interface Props {
  view: View
  setView: (view: View) => void
}

const boxStyle = { height: 'auto' }
const logoStyle = { marginBottom: 2 }

const formProps = { noValidate: true }

const errorFieldSx = {
  '& label': { color: 'error.main' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
  },
}

export function Main(props: Props) {
  const { view, setView } = props

  const texts = useTexts()
  const query = useQueryParameters()
  const initialValues = useLoginInitialValues()
  const throttle = useLoginThrottle()

  // held in state, not a ref: leaving and re-entering the login view mounts a
  // fresh form, so the disable effect below has to re-run against the new instance
  const [formApi, setFormApi] = useState<LoginFormApi | null>(null)

  // prefilled when the user is sent to Forgot Password because the password equals the username
  const [forgotPasswordInitialValues, setForgotPasswordInitialValues] = useState<{
    username: string
  }>()

  const goToForgotPassword = useCallback(() => {
    setForgotPasswordInitialValues(undefined)
    setView('forgot-password')
  }, [setView])

  const goToForgotPasswordWithUsername = useCallback(
    (username: string) => {
      setForgotPasswordInitialValues({ username })
      setView('forgot-password')
    },
    [setView],
  )

  const { submitLogin, forgotPassword } = useLoginActions(query, goToForgotPasswordWithUsername)

  const goToLogin = useCallback(() => {
    setView('login')
  }, [setView])

  // hand the whole form back the moment the lock expires — the countdown in
  // `useLoginThrottle` drives the phase change, so no refresh is needed
  const locked = throttle.phase === 'locked-live' || throttle.phase === 'locked-static'
  useEffect(() => {
    formApi?.setDisabled(locked)
  }, [formApi, locked])

  const fieldProps = useMemo(() => {
    // the fields stay red for as long as a credentials failure is on screen
    const showFieldError = throttle.phase === 'error' || throttle.phase === 'warning'
    const sx = showFieldError ? errorFieldSx : undefined

    return {
      username: { textfieldProps: { sx } },
      password: { textfieldProps: { sx } },
      submitButton: { disabled: locked },
    }
  }, [throttle.phase, locked])

  const handleLogin = useCallback(
    async (data: LoginFormState) => {
      if (locked) return

      const result = await submitLogin(data)

      if (result.outcome === 'success') {
        throttle.registerSuccess()
        return
      }

      if (result.outcome === 'throttled') {
        throttle.registerFailure(result.failure)
      }
    },
    [submitLogin, throttle, locked],
  )

  switch (view) {
    case 'login':
      return (
        <Box height={580} maxWidth={340}>
          <LoginFormContainer
            logo={<RouteManager width={340} sx={logoStyle} />}
            title={texts.login.logo}
            form={
              <Stack spacing={2}>
                {throttle.phase !== 'none' && (
                  <ThrottleAlerts throttle={throttle} onResetPassword={goToForgotPassword} />
                )}
                <LoginForm
                  texts={texts.login}
                  sx={boxStyle}
                  testId="login-form"
                  onLogin={handleLogin}
                  onFormApi={setFormApi}
                  initialValues={initialValues}
                  hideRememberMe
                  fieldProps={fieldProps}
                  formProps={formProps}
                />
              </Stack>
            }
            actions={
              <Stack width="100%" alignItems="center">
                <LoginCallToAction
                  label={texts.login.goToForgotPassword.label}
                  action={goToForgotPassword}
                />
              </Stack>
            }
          />
        </Box>
      )

    case 'forgot-password':
      return (
        <Box height={580} maxWidth={340}>
          <LoginFormContainer
            logo={<RouteManager width={340} sx={logoStyle} />}
            title={texts.forgotPassword.logo}
            form={
              <ForgotPasswordForm
                sx={boxStyle}
                texts={texts.forgotPassword}
                testId="login-form"
                onSubmit={forgotPassword}
                initialValues={forgotPasswordInitialValues}
              />
            }
            actions={
              <Stack width="100%" alignItems="center" paddingX={1} spacing={1}>
                <Typography textAlign="center">{texts.forgotPassword.forgotDescription}</Typography>
                <LoginCallToAction
                  description={texts.forgotPassword.goToLoginDescription}
                  label={texts.forgotPassword.goToLogin.label}
                  action={goToLogin}
                />
              </Stack>
            }
          />
        </Box>
      )
  }
}
